import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Building, Trash2, Mail, Phone, MapPin, Search } from 'lucide-react';
import { PappersLookup, type PappersLookupResult } from '@/components/ui/PappersLookup';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

export default function Clients() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pappersData, setPappersData] = useState<PappersLookupResult | null>(null);
  const queryClient = useQueryClient();

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createClient = useMutation({
    mutationFn: async (formData: FormData) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      const { error } = await supabase.from('clients').insert({
        organization_id: profile.organization_id,
        name: formData.get('name') as string,
        legal_name: formData.get('legal_name') as string || null,
        address: formData.get('address') as string || null,
        email: formData.get('email') as string || null,
        phone: formData.get('phone') as string || null,
        siret: formData.get('siret') as string || null,
        vat_number: formData.get('vat_number') as string || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setIsOpen(false);
      setPappersData(null);
      toast.success('Client créé avec succès');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteClient = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('clients').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client supprimé');
    },
    onError: (error: Error) => {
      if (error.message.includes('foreign key') || error.message.includes('violates')) {
        toast.error('Impossible de supprimer ce client car il a des SCI associées');
      } else {
        toast.error(error.message);
      }
    },
  });

  const columns = [
    { header: 'Nom', accessorKey: 'name' as const },
    { header: 'Raison sociale', accessorKey: 'legal_name' as const },
    { header: 'SIRET', accessorKey: 'siret' as const },
    { header: 'N° TVA', accessorKey: 'vat_number' as const },
    { 
      header: 'Contact', 
      accessorKey: (row: any) => (
        <div className="flex flex-col gap-1 text-sm">
          {row.email && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Mail className="h-3 w-3" />
              {row.email}
            </div>
          )}
          {row.phone && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Phone className="h-3 w-3" />
              {row.phone}
            </div>
          )}
        </div>
      )
    },
    { 
      header: 'Adresse', 
      accessorKey: (row: any) => (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          {row.address && (
            <>
              <MapPin className="h-3 w-3" />
              {row.address}
            </>
          )}
        </div>
      )
    },
    {
      header: 'Actions',
      accessorKey: (row: any) => (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2 text-destructive hover:text-destructive">
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer le client</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer le client "{row.name}" ? Cette action est irréversible.
                Si ce client a des SCI associées, vous devrez d'abord les supprimer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteClient.mutate(row.id)}
                disabled={deleteClient.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteClient.isPending ? 'Suppression...' : 'Supprimer'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ),
    },
  ];

  const filteredClients = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return clients;
    return clients.filter((client: any) => {
      const name = (client.name || '').toLowerCase();
      const legalName = (client.legal_name || '').toLowerCase();
      const email = (client.email || '').toLowerCase();
      const phone = (client.phone || '').toLowerCase();
      const address = (client.address || '').toLowerCase();
      return name.includes(term) || legalName.includes(term) || email.includes(term) || phone.includes(term) || address.includes(term);
    });
  }, [clients, searchTerm]);

  return (
    <AppLayout title="Clients" description="Gérez vos clients (cabinets comptables, particuliers)">
      <PageHeader
        title="Clients"
        description={`${clients.length} client${clients.length > 1 ? 's' : ''}`}
        actions={
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button><Plus className="mr-2 h-4 w-4" /> Nouveau client</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nouveau client</DialogTitle>
              </DialogHeader>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createClient.mutate(new FormData(e.currentTarget));
                }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label>Rechercher via Pappers</Label>
                  <PappersLookup
                    onSelect={(result) => {
                      setPappersData(result);
                    }}
                    placeholder="Rechercher une entreprise (nom ou SIREN)..."
                  />
                  <p className="text-xs text-muted-foreground">Remplissage automatique du SIRET, TVA, et adresse</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nom du client *</Label>
                  <Input id="name" name="name" placeholder="Cabinet Comptable XYZ" required defaultValue={pappersData?.name || ''} key={pappersData?.name || 'name'} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="legal_name">Raison sociale</Label>
                  <Input id="legal_name" name="legal_name" placeholder="Cabinet Comptable XYZ SARL" defaultValue={pappersData?.legalForm ? `${pappersData.name} - ${pappersData.legalForm}` : ''} key={pappersData?.legalForm || 'legal'} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="siret">SIRET</Label>
                    <Input id="siret" name="siret" placeholder="14 chiffres" maxLength={14} defaultValue={pappersData?.siret || ''} key={pappersData?.siret || 'siret'} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vat_number">N° TVA Intracommunautaire</Label>
                    <Input id="vat_number" name="vat_number" placeholder="FRXX XXXXXXXXX" maxLength={15} defaultValue={pappersData?.vatNumber || ''} key={pappersData?.vatNumber || 'vat'} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" placeholder="contact@cabinet.fr" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input id="phone" name="phone" placeholder="01 23 45 67 89" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Adresse</Label>
                  <Input id="address" name="address" placeholder="123 Rue de Paris, 75001 Paris" defaultValue={pappersData ? [pappersData.address, pappersData.postalCode, pappersData.city].filter(Boolean).join(', ') : ''} key={pappersData?.address || 'addr'} />
                </div>
                <Button type="submit" className="w-full" disabled={createClient.isPending}>
                  {createClient.isPending ? 'Création...' : 'Créer le client'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Search */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <DataTable columns={columns} data={filteredClients} isLoading={isLoading} />
    </AppLayout>
  );
}
